import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    api, getToken, setToken, setCsrfToken, bootstrapTokenStore,
    getDeviceId, setRememberedIdentity,
} from '@/lib/api';
import useNativeBootstrap from '@/lib/useNativeBootstrap';

const AuthCtx = createContext(null);

/**
 * AuthContext also drives the Phase 17 PIN unlock gate.
 *
 * pinGate can be:
 *   - 'unlock'  → user (or device) has a PIN; must enter PIN to proceed
 *   - 'setup'   → user just OTP-logged-in and has no PIN on this device; offer setup
 *   - 'none'    → no PIN gate (either user dismissed setup, or unlock succeeded)
 *
 * Lifecycle:
 *   - Cold start with valid JWT  → fetch /me + /auth/pin/status →
 *       has_pin_on_device ? gate='unlock' : gate='none'
 *   - Cold start *without* a JWT → still check /auth/pin/device-status. If
 *       this device has a PIN bound to a known user, we engage the unlock
 *       gate and let the user re-authenticate via PIN alone (skipping the
 *       phone/email OTP re-prompt that used to be required after a token
 *       expiry). This is the canonical fintech UX: "set up PIN once, then
 *       unlock with just the PIN on this device".
 *   - After OTP login            → check pin status → has_pin? 'none' : 'setup'
 *   - completePinGate()          → 'none' (called after successful unlock or
 *                                  PIN-setup completion / skip)
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pinGate, setPinGate] = useState('none');
    // Device-level identity used by the unlock screen when we don't (yet)
    // have a valid JWT. Populated from /auth/pin/device-status.
    const [pinIdentity, setPinIdentity] = useState(null);

    useNativeBootstrap();

    const completePinGate = useCallback(() => { setPinGate('none'); setPinIdentity(null); }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await bootstrapTokenStore();
            if (cancelled) return;
            const t = getToken();
            // No JWT: still check whether this device has a registered PIN.
            // If yes, engage the unlock gate so the user goes straight to
            // the PIN keypad instead of being forced through OTP login.
            if (!t) {
                try {
                    const did = await getDeviceId();
                    if (did) {
                        const r = await api.pinDeviceStatus(did);
                        if (!cancelled && r.data?.has_pin_on_device && r.data?.identity) {
                            setPinIdentity(r.data.identity);
                            setPinGate('unlock');
                        }
                    }
                } catch (e) {
                    // device-status failures are non-fatal — just fall back
                    // to the normal "no token → onboarding" flow.
                    console.debug('pin device-status check skipped', e);
                }
                setLoading(false);
                return;
            }
            try {
                const r = await api.me();
                if (cancelled) return;
                const u = r.data.user;
                setUser(u);
                setRememberedIdentity(u);
                // Phase 17 — check if this device has a PIN. If yes, gate the app.
                try {
                    const did = await getDeviceId();
                    const statusRes = await api.pinStatus(did);
                    if (!cancelled && statusRes.data?.has_pin_on_device) {
                        setPinGate('unlock');
                    }
                } catch (e) {
                    // PIN status failures are non-fatal — proceed without the gate.
                    console.debug('pin status check skipped', e);
                }
            } catch (e) {
                setToken(null);
                if (!cancelled) setUser(null);
                // Token was rejected — but if the device still has a PIN,
                // engage the unlock gate (same fallback as no-token path).
                try {
                    const did = await getDeviceId();
                    if (did) {
                        const r = await api.pinDeviceStatus(did);
                        if (!cancelled && r.data?.has_pin_on_device && r.data?.identity) {
                            setPinIdentity(r.data.identity);
                            setPinGate('unlock');
                        }
                    }
                } catch (innerErr) {
                    console.debug('pin device-status fallback skipped', innerErr);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /**
     * Called by OTP login screens after a successful verify.
     * Decides whether to prompt PinSetup, or send the user directly to home.
     */
    const login = async (token, fetched, csrfToken = null) => {
        setToken(token);
        // V3.11 — mirror the server-issued CSRF token from the login
        // response body so we can attach the X-CSRF-Token header even
        // when document.cookie reads return empty (Capacitor WebView).
        if (csrfToken) setCsrfToken(csrfToken);
        let u = fetched;
        if (!u) {
            const r = await api.me();
            u = r.data.user;
        }
        setUser(u);
        setRememberedIdentity(u);
        // Check if this device already has a PIN for this user — if yes, skip setup.
        try {
            const did = await getDeviceId();
            const statusRes = await api.pinStatus(did);
            if (statusRes.data?.has_pin_on_device) {
                setPinGate('none');           // already configured; nothing to do
            } else {
                setPinGate('setup');          // proactive prompt
            }
        } catch (e) {
            console.debug('pin status check after login failed', e);
            setPinGate('none');
        }
    };

    const logout = async () => {
        try { await api.logout(); } catch (e) { console.warn('Logout API call failed (clearing local state anyway):', e); }
        setToken(null);
        setCsrfToken(null);
        setUser(null);
        setPinGate('none');
        setPinIdentity(null);
    };

    /**
     * Called from the PIN unlock screen when the user taps "Forgot PIN /
     * Use phone or email instead". Clears the unlock gate so the app drops
     * back to the OTP login flow with no PIN obstruction.
     */
    const abandonPinUnlock = useCallback(() => {
        setPinGate('none');
        setPinIdentity(null);
    }, []);

    const refreshUser = async () => {
        try {
            const r = await api.me();
            setUser(r.data.user);
            setRememberedIdentity(r.data.user);
        } catch (e) {
            console.warn('Failed to refresh user (will retry on next interaction):', e);
        }
    };

    const value = useMemo(
        () => ({
            user, setUser, login, logout, loading, refreshUser,
            pinGate, setPinGate, completePinGate,
            pinIdentity, abandonPinUnlock,
        }),
        [user, loading, pinGate, completePinGate, pinIdentity, abandonPinUnlock],
    );

    return (
        <AuthCtx.Provider value={value}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const v = useContext(AuthCtx);
    if (!v) throw new Error('useAuth must be used within AuthProvider');
    return v;
}
